"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";

export interface DotTerm {
  slug: string;
  term: string;
  /** Featured terms keep their label open at all times and sit in fixed spots. */
  featured?: boolean;
}

interface DotFieldProps {
  /** Each dot is an AI term linking to its glossary page. */
  terms: DotTerm[];
  className?: string;
}

const GOLDEN = Math.PI * (3 - Math.sqrt(5));

/** Fixed, well-spaced spots for the always-open featured terms (unit disc). */
const FEATURED_POS = [
  { ux: -0.46, uy: -0.5 },
  { ux: 0.5, uy: -0.46 },
  { ux: 0.64, uy: 0.24 },
  { ux: 0.0, uy: 0.6 },
  { ux: -0.64, uy: 0.18 },
];

interface Placed {
  ux: number;
  uy: number;
  featured: boolean;
}

/**
 * Deterministic layout shared by the server render (for base positions) and the
 * client animation: featured terms take fixed spots; the rest fill the disc with
 * a golden-angle (sunflower) spread.
 */
function layoutFor(terms: DotTerm[]): Placed[] {
  const otherCount = Math.max(1, terms.filter((t) => !t.featured).length);
  let fi = 0;
  let oi = 0;
  return terms.map((t) => {
    if (t.featured && fi < FEATURED_POS.length) {
      const p = FEATURED_POS[fi++];
      return { ux: p.ux, uy: p.uy, featured: true };
    }
    const i = oi++;
    const a = i * GOLDEN;
    const rr = Math.sqrt((i + 0.5) / otherCount) * 0.92;
    return { ux: Math.cos(a) * rr, uy: Math.sin(a) * rr, featured: false };
  });
}

/**
 * A calm, friendly hero motif: a soft circle of AI terms shown as dots that sway
 * gently from side to side. Five popular terms stay labelled at all times; the
 * rest reveal their name on hover or keyboard focus. Every dot links to its
 * glossary page. Base positions are server-rendered at fixed precision (so the
 * field appears spread immediately, works without JavaScript, and avoids a
 * hydration mismatch). Pauses off-screen / when hidden; respects
 * prefers-reduced-motion.
 */
export function DotField({ terms, className }: DotFieldProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const dotRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const layout = layoutFor(terms);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || terms.length === 0) return;
    const reduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduced) return; // Server-set base positions already place the dots.

    const placed = layoutFor(terms);
    const specs = placed.map((p, i) => {
      const room = Math.max(
        0,
        Math.sqrt(Math.max(0, 1 - p.uy * p.uy)) - Math.abs(p.ux),
      );
      return {
        // Featured dots barely move so their open labels stay readable.
        amp: p.featured ? Math.min(0.04, room * 0.4) : Math.min(0.16, room * 0.85),
        speed: 0.25 + (i % 5) * 0.08,
        phase: (i * 1.7) % (Math.PI * 2),
        bob: p.featured ? 1 : 1 + (i % 3),
        bphase: (i * 2.3) % (Math.PI * 2),
      };
    });

    let R = 0;
    let lastW = -1;
    let raf = 0;
    let visible = true;
    let t0 = 0;

    const measure = () => {
      R = wrap!.clientWidth * 0.42;
      lastW = wrap!.clientWidth;
    };

    function step(now: number) {
      if (!t0) t0 = now;
      const tt = (now - t0) / 1000;
      for (let i = 0; i < specs.length; i++) {
        const el = dotRefs.current[i];
        if (!el) continue;
        const s = specs[i];
        const dx = Math.sin(tt * s.speed + s.phase) * s.amp * R;
        const dy = Math.sin(tt * s.speed * 0.6 + s.bphase) * s.bob;
        el.style.transform = `translate(${dx}px, ${dy}px)`;
      }
      if (visible) raf = requestAnimationFrame(step);
    }

    const start = () => {
      cancelAnimationFrame(raf);
      t0 = 0;
      raf = requestAnimationFrame(step);
    };

    measure();
    start();

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          visible = e.isIntersecting;
          if (visible) start();
          else cancelAnimationFrame(raf);
        }
      },
      { threshold: 0.05 },
    );
    io.observe(wrap);

    const onVis = () => {
      if (!document.hidden && visible) start();
    };
    document.addEventListener("visibilitychange", onVis);

    let rzTimer = 0;
    const ro = new ResizeObserver(() => {
      if (Math.abs(wrap!.clientWidth - lastW) < 8) return;
      window.clearTimeout(rzTimer);
      rzTimer = window.setTimeout(measure, 180);
    });
    ro.observe(wrap);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      ro.disconnect();
      window.clearTimeout(rzTimer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [terms]);

  return (
    <div ref={wrapRef} className={cn("relative w-full", className)}>
      {/* Soft circle that frames the field. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 aspect-square w-[84%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/15 bg-primary/[0.03]"
      />
      {terms.map((t, i) => {
        const p = layout[i];
        return (
          <Link
            key={t.slug}
            href={`/glossary/${t.slug}`}
            prefetch={false}
            ref={(el) => {
              dotRefs.current[i] = el;
            }}
            // Fixed precision keeps the server and client strings identical.
            style={{
              left: `${(50 + p.ux * 42).toFixed(3)}%`,
              top: `${(50 + p.uy * 42).toFixed(3)}%`,
              willChange: "transform",
            }}
            className="group absolute -ml-3.5 -mt-3.5 flex size-7 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
            aria-label={`View ${t.term} in the AI glossary`}
          >
            <span
              className={cn(
                "rounded-full transition-all duration-200",
                p.featured
                  ? "size-2.5 bg-secondary"
                  : "size-2 bg-primary/75 group-hover:scale-[1.6] group-hover:bg-secondary group-focus-visible:scale-[1.6] group-focus-visible:bg-secondary",
              )}
            />
            <span
              className={cn(
                "pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md border bg-paper px-2 py-1 text-xs font-medium text-dark shadow-sm transition-opacity duration-150",
                p.featured
                  ? "border-secondary/40 opacity-100"
                  : "border-line opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
              )}
            >
              {t.term}
            </span>
          </Link>
        );
      })}
    </div>
  );
}
